import { FC, useMemo } from "react";

import {
  CreateGUFExpandedRealisedInstallationTask,
  EditGUFExpandedRealisedInstallationTask,
} from "@nens/brostar/uploadapi-client";

import { getApi } from "@/api/api-configuration";
import { getFormObjectValues, initializeFormEditingState } from "@/helpers/helpers";
import { useAfterFormSubmit } from "@/hooks/use-after-form-submit";
import { BrostarForm, useBrostarFormDataStorage } from "@/libs/brostar-form";
import { GufExpandedRealisedInstallationFormProps } from "@/types/form-types";

import { getGufExpandedRealisedInstallationSchema } from "../schema/guf-expand-realised-installation-schema";
import { useGufExpandedRealisedInstallationUiSchema } from "../schema/guf-expand-realised-installation-ui-schema";
import { validateGufExpandedRealisedInstallationForm } from "../schema/guf-validation";

const GufExpandedRealisedInstallationForm: FC<GufExpandedRealisedInstallationFormProps> = (
  props
) => {
  const api = getApi();
  const afterSubmit = useAfterFormSubmit();

  const { oldUploadTask, task, type } = getFormObjectValues(props);
  const { isEditing, getIsEditing } = initializeFormEditingState(props);

  const { formData, handleChange } = useBrostarFormDataStorage<
    CreateGUFExpandedRealisedInstallationTask | EditGUFExpandedRealisedInstallationTask
  >({
    key: "guf-expanded-realised-installation",
    task,
  });

  const uiSchema = useGufExpandedRealisedInstallationUiSchema(formData?.metadata.qualityRegime);

  const schema = useMemo(
    () => getGufExpandedRealisedInstallationSchema({ type, isEditing }),
    [type, isEditing]
  );

  const onSubmit = async () => {
    if (!formData) return;
    if (getIsEditing(formData)) {
      await api.editGUFExpandedRealisedInstallation(formData);
    } else {
      await api.createGUFExpandedRealisedInstallation(formData);
    }
    if (oldUploadTask?.uuid) api.apiUploadtasksDelete({ uuid: oldUploadTask.uuid });
    await afterSubmit();
  };

  return (
    <BrostarForm<
      CreateGUFExpandedRealisedInstallationTask | EditGUFExpandedRealisedInstallationTask
    >
      uploadTask={oldUploadTask}
      schema={schema}
      formData={formData}
      onChange={handleChange}
      uiSchema={uiSchema}
      onSubmit={onSubmit}
      customValidate={validateGufExpandedRealisedInstallationForm}
    />
  );
};

export { GufExpandedRealisedInstallationForm };
